import React from "react";
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { vi } from "date-fns/locale";
import { format, isValid, parseISO } from "date-fns";
import { LabeledField } from "@/__core/components/LabeledField";
import { sharedFormStyles } from "@/__core/styles/sharedFormStyles";
import JobTitleSelect from "../JobTitleSelect";
import AreaSelect from "../AreaSelect";
import { AttendanceType } from "@/modules/employees/models/Employee";
import { MISSIONS_OPTIONS } from "@/__core/constants/mission";
import { DASHBOARD_OPTIONS } from "@/__core/constants/dashboard";

type SimpleOption = {
  id: string | number;
  name: string;
};

interface WorkFormData {
  company?: string;
  department?: string;
  job_title?: string;
  start_date?: string;
  area?: string;
  resign_date?: string;
  attendance_type?: AttendanceType;
  missions?: string[];
  dashboards?: string[];
}



interface JobPositionSectionProps {
  formData: WorkFormData;
  errors: { [key: string]: string };
  hasGlobalRole: boolean;
  canEdit: boolean;
  safeCompanies: SimpleOption[];
  filteredDepartments: SimpleOption[];
  onChange: (field: string, value: any) => void;
}

const JobPositionSection: React.FC<JobPositionSectionProps> = ({
  formData,
  errors,
  canEdit,
  safeCompanies,
  filteredDepartments,
  onChange,
}) => {
  const startDateValue = formData.start_date ? parseISO(formData.start_date) : null;
  const resignDateValue = formData.resign_date ? parseISO(formData.resign_date) : null;
  const selectedMissionOptions = MISSIONS_OPTIONS.filter((option) =>
    Array.isArray(formData.missions) ? formData.missions.includes(option.code) : false
  );
  const selectedDashboardOptions = DASHBOARD_OPTIONS.filter((option) =>
    Array.isArray(formData.dashboards) ? formData.dashboards.includes(option.code) : false
  );

  return (
    <Paper sx={{ ...sharedFormStyles.formCard, mb: 2 }}>
      <Typography sx={sharedFormStyles.sectionTitle}>Thông tin công việc</Typography>

      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
        <Grid container rowSpacing={1} columnSpacing={5}>
          <Grid size={{ xs: 12, md: 6 }}>
            <LabeledField label="Công ty" required labelWidth={100}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.company || ""}
                  onChange={(e) => onChange("company", e.target.value)}
                  error={!!errors.company}
                  disabled
                >
                  {safeCompanies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LabeledField>

            <LabeledField label="Phòng ban" labelWidth={100}>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.department || ""}
                  onChange={(e) => onChange("department", e.target.value)}
                  disabled={!formData.company || !canEdit}
                >
                  {filteredDepartments.map((dept) => (
                    <MenuItem key={dept.id} value={String(dept.id)}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </LabeledField>

            
            <LabeledField label="Chấm công" labelWidth={100}>
              <Box sx={sharedFormStyles.radioGroupRow}>
                <Switch
                  size="small"
                  disabled={!canEdit}
                  checked={(formData.attendance_type || "attendance") === "attendance"}
                  onChange={(_e, checked) =>
                    onChange("attendance_type", (checked ? "attendance" : "no_attendance") as AttendanceType)
                  }
                />
              </Box>
            </LabeledField>

            <LabeledField label="Nhiệm vụ" labelWidth={100} alignItems="end">
              <FormControl fullWidth size="small">
                <Autocomplete
                  multiple
                  fullWidth
                  options={MISSIONS_OPTIONS}
                  value={selectedMissionOptions}
                  onChange={(_event, newValue) => onChange("missions", newValue.map((item) => item.code))}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  disabled={!canEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Chọn nhiệm vụ"
                      sx={sharedFormStyles.multiSelectInput}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.code}
                        label={option.label}
                        size="small"
                        sx={sharedFormStyles.multiSelectChip}
                      />
                    ))
                  }
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Typography variant="body2" sx={sharedFormStyles.multiSelectOptionText}>
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  }}
                />
              </FormControl>
            </LabeledField>

            
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <JobTitleSelect
              value={formData.job_title || ""}
              onChange={(value) => onChange("job_title", value)}
              error={!!errors.job_title}
              labelWidth={100}
              disabled={!canEdit}
            />
            <AreaSelect
              value={formData.area || ""}
              onChange={(value) => onChange("area", value)}
              labelWidth={100}
              disabled={!canEdit}
            />
            <LabeledField label="Ngày vào làm" labelWidth={100}>
              <DatePicker
                value={startDateValue && isValid(startDateValue) ? startDateValue : null}
                onChange={(value) => onChange("start_date", value && isValid(value) ? format(value, "yyyy-MM-dd") : "")}
                disabled={!canEdit}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </LabeledField>
            <LabeledField label="Ngày nghỉ việc" labelWidth={100}>
              <DatePicker
                value={resignDateValue && isValid(resignDateValue) ? resignDateValue : null}
                onChange={(value) => onChange("resign_date", value && isValid(value) ? format(value, "yyyy-MM-dd") : "")}
                disabled={!canEdit}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                  },
                }}
              />
            </LabeledField>

           
          </Grid>

          <Grid size={{ xs: 12 }}>
                <LabeledField label="Dashboard" labelWidth={100} alignItems="end">
              <FormControl fullWidth size="small">
                <Autocomplete
                  multiple
                  fullWidth
                  options={DASHBOARD_OPTIONS}
                  value={selectedDashboardOptions}
                  onChange={(_event, newValue) => onChange("dashboards", newValue.map((item) => item.code))}
                  getOptionLabel={(option) => option.label}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  disabled={!canEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Chọn dashboard"
                      sx={sharedFormStyles.multiSelectInput}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.code}
                        label={option.label}
                        size="small"
                        sx={sharedFormStyles.multiSelectChip}
                      />
                    ))
                  }
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        <Typography variant="body2" sx={sharedFormStyles.multiSelectOptionText}>
                          {option.label}
                        </Typography>
                      </Box>
                    );
                  }}
                />
              </FormControl>
            </LabeledField>
          </Grid>
        </Grid>
      </LocalizationProvider>
    </Paper>
  );
};

export default JobPositionSection;
